/**
 * Agent Browser — the live shared Chromium pane (handoff §7): the surface
 * where the user sits beside the agent while it navigates portals.
 *
 * Renders in three places, all sharing this one component:
 *   - the `/agent-browser` route + the `agentBrowser` focus-pane slot (full
 *     chrome: Back + title + Pause/Close);
 *   - EMBEDDED as the first tab of the Agent pane (`AgentSurface`), via
 *     `embedded` — which sheds the Back button and the "Lexiun Browser" title
 *     (both redundant under the Agent pane's own title bar + tab strip) while
 *     keeping the Pause/Close controls, the turn-taking pill, and the security
 *     note. Everything else — auto-launch, the noVNC canvas, every non-running
 *     overlay — is identical, so the two entry points never diverge.
 *
 * Option B (2026-05-31, source of truth: lexruntime-openclaw-settings-
 * implementation-plan.md §4-sexies). The multi-profile machinery stays
 * fully wired on the runtime side, but is hidden from the lawyer.
 *
 * Layout (2026-06-01 redesign — "browser-shines"):
 *
 *   ┌─ topbar (h-12) ──────────────────────────────────────────┐
 *   │ ← Back   🌐 Lexiun Browser  profile · status pill        │
 *   │                              [Pause agent]  [Close]      │
 *   ├──────────────────────────────────────────────────────────┤
 *   │                                                          │
 *   │           BROWSER PANE — fills the whole main            │
 *   │           OR centered overlay for non-running states     │
 *   │           (loading, locked, not-provisioned, error,      │
 *   │            auto-launching).                              │
 *   │                                                          │
 *   ├──────────────────────────────────────────────────────────┤
 *   │ ⚠ Compact security note                                 │
 *   └──────────────────────────────────────────────────────────┘
 *
 * The browser content is the centerpiece. The two slim bars carry
 * everything else — identity, turn-taking status, action buttons,
 * security note — so the pane gets maximum real estate.
 *
 * Empty / disabled cases (all centered overlays IN the main area, no
 * push of the browser):
 *   - `lockedByOrg === true` → existing `lockedByOrg` copy.
 *   - `profiles.length === 0` → `agentBrowser.notProvisioned*` copy.
 *   - loading / loadError / loadRouteNotFound → spinner / error / amber
 *     upgrade-required banner with retry.
 *   - busy === "launch" && !activeProfile → autoLaunching spinner.
 *
 * Deferred (P4.H): per-tab indicator + richer session.tool semantics
 * (current D3 wiring handles `lx_browser_navigate` start/end only).
 *
 * Handoff hard-rules honored:
 *   - WS URL + `?token=` never logged, never put in error toasts.
 *   - Short-TTL ticket refresh handled by NoVncPane (~50 s default).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEventStream } from "@/hooks/useEventStream";
import { useTranslation } from "react-i18next";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  Globe,
  Loader2,
  Pause,
  Play,
  Power,
} from "lucide-react";
import { Card } from "@/components/ui/Card";
import { useToastStore } from "@/stores/useToastStore";
import {
  getBrowserStatus,
  getDevVncUrl,
  getOpenclawConfig,
  launchBrowserProfile,
  listBrowserProfiles,
  LxOpenclawConfigError,
  pauseBrowserProfile,
  resumeBrowserProfile,
  stopBrowserProfile,
  type BrowserProfile,
  type BrowserStatus,
  type DevVncUrlResponse,
  type OpenclawConfig,
} from "@/api/lx-openclaw-config";
import { isDisabledByOrg } from "@/lib/openclaw-policy";
import { cn } from "@/lib/utils";
import { NoVncPane, type NoVncStatus } from "@/components/agent-browser/NoVncPane";
import { LearnMoreDialog } from "@/components/agent-browser/LearnMoreDialog";

export function AgentBrowserLayout({ embedded = false }: { embedded?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const addToast = useToastStore((s) => s.addToast);

  const [config, setConfig] = useState<OpenclawConfig | null>(null);
  const [profiles, setProfiles] = useState<BrowserProfile[]>([]);
  const [status, setStatus] = useState<BrowserStatus | null>(null);
  const [devVnc, setDevVnc] = useState<DevVncUrlResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Distinct from `loadError` so the render path can surface the
  // §12-bis upgrade-required banner instead of the generic "load
  // failed" copy when the user's runtime is on an older image that
  // doesn't have the /browser/status or /config/get routes.
  const [loadRouteNotFound, setLoadRouteNotFound] = useState(false);
  const [busy, setBusy] = useState<
    "launch" | "stop" | "pause" | "resume" | null
  >(null);
  const [paused, setPaused] = useState(false);
  // The user CLOSED the browser from inside the pane, and we have not
  // launched since. Distinct from "no active profile" — which is also the
  // cold-start state that SHOULD auto-launch.
  //
  // ⚠ Without this the pane lies. `autoLaunched` is a once-per-MOUNT latch, so
  // after an in-pane Close the auto-launch effect is permanently disarmed
  // (activeProfile goes null, every other guard passes, and `autoLaunched.current`
  // returns early) — no `/launch` is ever POSTed. Meanwhile the render fell
  // through to the catch-all `!showNoVnc` branch and showed
  // "Starting the Lexiun Browser…" forever: the UI advertised a launch it had
  // never requested. (Flipping to another PA tab and back remounts, which
  // resets the latch and "fixes" it — which is why this read as flaky.)
  // Reported by `go-live` off a prod trace on `aad85ad32`, 2026-08-09.
  //
  // The RULING is await-user, not re-arm: the user just said "close this", and
  // silently bringing Chromium back would fight the intent they expressed. So
  // the honest post-close state is a Launch button.
  const [closedByUser, setClosedByUser] = useState(false);
  const [vncStatus, setVncStatus] = useState<NoVncStatus>("idle");
  // D3: live readout — set by `session.tool` events from the runtime for
  // `lx_browser_navigate` invocations. `phase: "start"` arms it; `"end"`
  // clears. A safety timeout clears after `AGENT_ACTIVITY_TTL_MS` in
  // case the end event drops (lossy WS) so the strip doesn't stick on
  // "Agent is navigating" indefinitely.
  const [agentNavigatingUrl, setAgentNavigatingUrl] = useState<string | null>(
    null,
  );
  const agentClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const AGENT_ACTIVITY_TTL_MS = 60_000;

  // Learn-more dialog — local useState (page-scoped, single trigger).
  // The triggerRef is passed to LearnMoreDialog so focus returns to
  // the bottombar button on close (a11y nicety, mirrors the canonical
  // info-dialog pattern).
  const [learnMoreOpen, setLearnMoreOpen] = useState(false);
  const learnMoreButtonRef = useRef<HTMLButtonElement>(null);

  useEventStream("session.tool", (payload) => {
    const data = (payload as { data?: Record<string, unknown> }).data;
    if (!data || data.toolName !== "browser") return;
    // Native upstream browser tool is single-name, multi-action. The
    // ActorStrip live readout only lights up on the "navigate"
    // sub-action — other actions (click, type, snapshot, …) are
    // intra-page operations whose result the user sees directly in
    // the noVNC framebuffer, no readout needed.
    const input = data.toolInput as
      | { action?: unknown; url?: unknown }
      | undefined;
    if (input?.action !== "navigate") return;
    if (agentClearTimerRef.current) {
      clearTimeout(agentClearTimerRef.current);
      agentClearTimerRef.current = null;
    }
    if (data.phase === "start") {
      const url = typeof input?.url === "string" ? input.url : null;
      setAgentNavigatingUrl(url);
      agentClearTimerRef.current = setTimeout(() => {
        setAgentNavigatingUrl(null);
        agentClearTimerRef.current = null;
      }, AGENT_ACTIVITY_TTL_MS);
    } else if (data.phase === "end") {
      setAgentNavigatingUrl(null);
    }
  });

  useEffect(() => {
    return () => {
      if (agentClearTimerRef.current) clearTimeout(agentClearTimerRef.current);
    };
  }, []);

  const reload = useCallback(async () => {
    setLoadError(null);
    setLoadRouteNotFound(false);
    try {
      // GET /browser/profiles FIRST, alone: it owns the runtime's
      // self-healing auto-create of the `default` profile (Option B,
      // runtime commit 033caa88). Running it before /config/get and
      // /browser/status guarantees a cold-start state dir (fresh
      // user provisioning, wiped NFS state) is healed before the
      // other two read it — Promise.all'ing everything would race
      // the auto-create write against the config read and cold-start
      // into the "not provisioned" dead end.
      const p = await listBrowserProfiles();
      const [c, s, dev] = await Promise.all([
        getOpenclawConfig(),
        getBrowserStatus(),
        // The dev-vnc-url endpoint may legitimately fail on a runtime
        // that doesn't expose the docker host-port mapping (prod). Treat
        // a failure as "no dev URL available" rather than blocking the
        // whole load — the pane falls back to the runtime's reported
        // vnc.endpoint, which is the only path that'll work once the
        // prod tunnel ships in P4.D.W.
        getDevVncUrl().catch(() => null),
      ]);
      setConfig(c);
      setProfiles(p);
      setStatus(s);
      setDevVnc(dev);
    } catch (err) {
      if (err instanceof LxOpenclawConfigError && err.routeNotFound) {
        setLoadRouteNotFound(true);
      }
      setLoadError(err instanceof Error ? err.message : String(err));
      throw err;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        await reload();
      } catch {
        /* surfaced via loadError state */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const lockedByOrg = config ? isDisabledByOrg(config.org, "browser") : false;
  const activeId = status?.activeProfileId ?? null;
  const activeProfile = profiles.find((p) => p.id === activeId) ?? null;
  // Option B: the lawyer never picks a profile. Pick the runtime's
  // auto-created `"default"` profile (or, defensively, the first one in
  // the list — covers a future where the auto-create label changes).
  const defaultProfile: BrowserProfile | null = useMemo(() => {
    if (profiles.length === 0) return null;
    return profiles.find((p) => p.label === "default") ?? profiles[0];
  }, [profiles]);
  // The VNC endpoint and runtime liveness. Resolution order (P4.D.W):
  //   1. `status.vnc.endpoint` — the lexserver-tunneled path
  //      (`/ws/runtime/<userId>/api/lx/openclaw/browser/vnc/ws`, rewritten in
  //      lx-openclaw-config). This is the prod path AND the dev path now: both
  //      ride the lexserver tunnel + in-container vnc-upgrade-shim, so localdev
  //      exercises exactly what prod does. `null` when the browser is disabled.
  //   2. `devVnc.wsUrl` — the direct docker host-port (P4.D.dev →
  //      ws://127.0.0.1:<derived>), kept as a DEBUG FALLBACK only.
  // When BOTH resolve to null AND we have an active profile, we render an
  // explicit "live view not yet available" overlay — never a silent infinite
  // "Connecting…" spinner. See ai-docs/lexruntime-noVNC-monkey-patch.md.
  const vncEndpoint = status?.vnc?.endpoint ?? devVnc?.wsUrl ?? null;

  const onOpen = useCallback(
    async (id: string) => {
      if (busy || lockedByOrg) return;
      setBusy("launch");
      // Clear the post-close latch as the launch STARTS, not after it lands:
      // the render keys the "Starting…" spinner off `busy === "launch"`, and
      // leaving the latch set would flash the closed-state card mid-launch.
      setClosedByUser(false);
      try {
        await launchBrowserProfile(id);
        await reload();
        setPaused(false);
      } catch (err) {
        handleErr(err, "launch");
      } finally {
        setBusy(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [busy, lockedByOrg, reload],
  );

  // Option B: auto-launch the default profile on first mount once the
  // config + status have loaded. The runtime's lazy-launch path will
  // bring up Xvfb + Chromium on first connection; we just need to
  // ensure the activeProfileId is set so vnc-ticket mints + the noVNC
  // pane mounts. Guards: don't fire while loading, while a busy state
  // is active, when the org has the kill-switch on, OR when no profile
  // exists. Auto-launch fires AT MOST once per mount via `autoLaunchedRef`
  // so a downstream `reload()` doesn't re-trigger it.
  const autoLaunched = useMemo(() => ({ current: false }), []);
  // status.chromium is null when the supervisor has no live Chromium —
  // either because the user never launched, or because the container
  // was rolled / Chromium crashed since the last activeProfileId
  // assignment. The persisted activeProfileId outlives Chromium PIDs
  // (it lives in settings.json on NFS), so "activeProfile is set" is
  // not the same as "Chromium is alive". We auto-launch when either
  // condition is true: no active profile yet, OR the active profile's
  // supervisor record is missing.
  const supervisorChromiumAlive = status?.chromium != null;
  useEffect(() => {
    if (loading) return;
    if (loadError || loadRouteNotFound) return;
    if (lockedByOrg) return;
    if (busy) return;
    if (!defaultProfile) return;
    if (activeProfile && supervisorChromiumAlive) return;
    // An explicit in-pane Close means "leave it closed" — see `closedByUser`.
    if (closedByUser) return;
    if (autoLaunched.current) return;
    autoLaunched.current = true;
    void onOpen(defaultProfile.id);
  }, [
    loading,
    loadError,
    loadRouteNotFound,
    lockedByOrg,
    busy,
    activeProfile,
    supervisorChromiumAlive,
    defaultProfile,
    closedByUser,
    onOpen,
    autoLaunched,
  ]);

  async function onClose() {
    if (busy || !activeId) return;
    setBusy("stop");
    try {
      await stopBrowserProfile(activeId);
      await reload();
      setPaused(false);
      // Only on SUCCESS. A failed stop leaves Chromium running, and latching
      // "closed" there would show a Launch button for a browser that is
      // still live.
      setClosedByUser(true);
    } catch (err) {
      handleErr(err, "stop");
    } finally {
      setBusy(null);
    }
  }

  async function onTogglePause() {
    if (busy || !activeId) return;
    const next = !paused;
    setBusy(next ? "pause" : "resume");
    try {
      if (next) await pauseBrowserProfile(activeId);
      else await resumeBrowserProfile(activeId);
      setPaused(next);
    } catch (err) {
      handleErr(err, next ? "pause" : "resume");
    } finally {
      setBusy(null);
    }
  }

  function handleErr(err: unknown, action: string) {
    if (err instanceof LxOpenclawConfigError && err.routeNotFound) {
      addToast({
        kind: "error",
        text: t("settings.openclaw.upgradeRequired.toast"),
        autoDismissMs: 8000,
      });
      return;
    }
    addToast({
      kind: "error",
      text: t("agentBrowser.actionFailed", { action }),
      autoDismissMs: 6000,
    });
  }

  const onRetryLoad = () => {
    setLoading(true);
    void reload().finally(() => setLoading(false));
  };

  const showNoVnc =
    !loading &&
    !loadError &&
    !loadRouteNotFound &&
    !lockedByOrg &&
    Boolean(defaultProfile) &&
    Boolean(activeProfile) &&
    Boolean(vncEndpoint);

  return (
    <div className="flex flex-1 flex-col bg-cream-50 dark:bg-coffee-950">
      {/* ── Topbar ─────────────────────────────────────────────────── */}
      <header
        data-testid="agent-browser-topbar"
        className="flex h-12 shrink-0 items-center justify-between gap-3 border-b border-cream-200/60 bg-cream-50 px-4 dark:border-coffee-700/60 dark:bg-coffee-900"
      >
        <div className="flex min-w-0 items-center gap-3">
          {/* Back + title are redundant when embedded as the Agent-pane's
              Browser tab — the pane's own title bar + tab strip already name it. */}
          {!embedded && (
            <>
              <button
                type="button"
                data-testid="agent-browser-back"
                onClick={() => navigate(-1)}
                aria-label={t("settings.back")}
                title={t("settings.back")}
                className="flex h-8 w-8 items-center justify-center rounded-md text-coffee-600 transition-colors hover:bg-cream-100/60 hover:text-coffee-900 dark:text-cream-400 dark:hover:bg-coffee-800/60 dark:hover:text-cream-100"
              >
                <ArrowLeft size={16} />
              </button>
              <div className="flex items-center gap-2 text-coffee-900 dark:text-gold-400">
                <Globe size={18} strokeWidth={1.75} aria-hidden />
                <span className="font-serif-display italic text-body-lg text-coffee-900 dark:text-cream-100">
                  {t("agentBrowser.title")}
                </span>
              </div>
            </>
          )}
          {activeProfile && (
            <span
              data-testid="agent-browser-profile-chip"
              className="hidden truncate text-caption text-coffee-500 dark:text-cream-500 md:inline"
            >
              · {t("agentBrowser.actor.profileLabel", { label: activeProfile.label })}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {activeProfile && (
            <ActorStatusPill
              vncStatus={vncStatus}
              paused={paused}
              agentNavigatingUrl={agentNavigatingUrl}
            />
          )}
          {activeProfile && (
            <>
              <button
                type="button"
                data-testid="agent-browser-pause-toggle"
                disabled={busy !== null}
                onClick={() => void onTogglePause()}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors",
                  busy !== null
                    ? "cursor-not-allowed border-cream-200/60 text-cream-500 dark:border-coffee-700/60"
                    : paused
                      ? "border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-600 hover:border-emerald-600"
                      : "border-coffee-800 bg-coffee-800 text-cream-50 hover:bg-coffee-900 hover:border-coffee-900 dark:border-cream-100 dark:bg-cream-100 dark:text-coffee-900 dark:hover:bg-cream-50 dark:hover:border-cream-50",
                )}
              >
                {busy === "pause" || busy === "resume" ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : paused ? (
                  <Play size={12} />
                ) : (
                  <Pause size={12} />
                )}
                <span className="hidden sm:inline">
                  {paused
                    ? t("agentBrowser.actor.resumeButton")
                    : t("agentBrowser.actor.pauseButton")}
                </span>
              </button>
              <button
                type="button"
                data-testid="agent-browser-close"
                disabled={busy !== null}
                onClick={() => void onClose()}
                aria-label={t("agentBrowser.actor.closeButton")}
                title={t("agentBrowser.actor.closeButton")}
                className="inline-flex items-center gap-1.5 rounded-md border border-cream-200/60 px-2.5 py-1 text-xs font-medium text-coffee-600 transition-colors hover:bg-cream-50 hover:text-coffee-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-coffee-700/60 dark:text-cream-400 dark:hover:bg-coffee-800/60 dark:hover:text-cream-100"
              >
                {busy === "stop" ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Power size={12} />
                )}
                <span className="hidden sm:inline">
                  {t("agentBrowser.actor.closeButton")}
                </span>
              </button>
            </>
          )}
        </div>
      </header>

      {/* ── Main browser surface ────────────────────────────────────
           Single-purpose region: either the live noVNC canvas OR a
           centered overlay for non-running states. Coffee-900 base
           so the canvas borderless-edges blend with the chrome. */}
      <div
        data-testid="agent-browser-main"
        className="relative flex flex-1 items-center justify-center overflow-hidden bg-coffee-900"
      >
        {loading ? (
          <CenteredOverlay tone="info" testId="agent-browser-loading">
            <Loader2 size={20} className="animate-spin text-cream-300" aria-hidden />
            <p className="text-body-sm text-cream-200">{t("agentBrowser.loading")}</p>
          </CenteredOverlay>
        ) : loadRouteNotFound ? (
          // §12-bis.4 upgrade-required UX — runtime image predates
          // openclaw-settings routes. Surface explicitly so the user
          // doesn't read it as a generic "load failed".
          <CenteredOverlay tone="warning" testId="agent-browser-upgrade-required" role="alert">
            <AlertTriangle size={20} className="text-amber-300" aria-hidden />
            <p className="text-body-sm text-amber-200">
              {t("settings.openclaw.upgradeRequired.banner")}
            </p>
            <button
              type="button"
              data-testid="agent-browser-upgrade-required-retry"
              onClick={onRetryLoad}
              className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-amber-300/70 bg-amber-50/10 px-3 py-1 text-xs font-medium text-amber-200 transition-colors hover:bg-amber-50/20"
            >
              {t("settings.openclaw.upgradeRequired.retry")}
            </button>
          </CenteredOverlay>
        ) : loadError ? (
          <CenteredOverlay tone="error" testId="agent-browser-load-error">
            <AlertCircle size={20} className="text-red-400" aria-hidden />
            <p className="text-body-sm text-red-300">{t("agentBrowser.loadFailed")}</p>
            <button
              type="button"
              onClick={onRetryLoad}
              className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-cream-200/40 px-3 py-1 text-xs font-medium text-cream-200 transition-colors hover:bg-cream-50/10"
            >
              {t("agentBrowser.retry")}
            </button>
          </CenteredOverlay>
        ) : lockedByOrg ? (
          <CenteredOverlay tone="info" testId="agent-browser-locked">
            <Power size={20} className="text-cream-400" aria-hidden />
            <p className="text-body-sm text-cream-200">{t("agentBrowser.lockedByOrg")}</p>
          </CenteredOverlay>
        ) : !defaultProfile ? (
          // Option B: the runtime hasn't auto-provisioned a profile yet
          // (pre-P4.D auto-create image, or org-policy denied before
          // the user was created). Lawyer cannot self-fix; copy directs
          // them to Lexiun support.
          <CenteredOverlay tone="info" testId="agent-browser-not-provisioned">
            <Globe size={32} strokeWidth={1.25} className="text-cream-400" aria-hidden />
            <h3 className="font-serif-display italic text-display-sm text-cream-100">
              {t("agentBrowser.notProvisionedHeadline")}
            </h3>
            <p className="max-w-md text-body-sm text-cream-300">
              {t("agentBrowser.notProvisionedHelp")}
            </p>
          </CenteredOverlay>
        ) : activeProfile && !vncEndpoint ? (
          // Active profile exists (so the runtime IS ready to serve) but
          // no VNC endpoint resolved — the P4.D.W WebSocket-upgrade
          // handler isn't wired in this deploy. Render an explicit
          // error overlay; no infinite spinner. (Once P4.D.W ships,
          // status.vnc switches from null to a real endpoint and the
          // pane mounts on the next reload.)
          <CenteredOverlay tone="warning" role="alert" testId="agent-browser-live-view-unavailable">
            <AlertCircle size={32} strokeWidth={1.25} className="text-cream-400" aria-hidden />
            <h3 className="font-serif-display italic text-display-sm text-cream-100">
              {t("agentBrowser.liveViewUnavailableHeadline")}
            </h3>
            <p className="max-w-md text-body-sm text-cream-300">
              {t("agentBrowser.liveViewUnavailableHelp")}
            </p>
          </CenteredOverlay>
        ) : closedByUser && busy !== "launch" ? (
          // CLOSED BY THE USER, awaiting their next move. This branch exists
          // because the spinner below used to swallow this state and claim a
          // launch was starting — while the auto-launch latch stayed disarmed,
          // so no `/launch` was ever sent and the copy never came true.
          // A state with no pending work must offer the action, not a spinner.
          <CenteredOverlay tone="info" testId="agent-browser-closed">
            <p className="text-body-sm text-cream-200">{t("agentBrowser.closedHelp")}</p>
            <button
              type="button"
              onClick={() => defaultProfile && void onOpen(defaultProfile.id)}
              disabled={!defaultProfile || busy != null}
              className="rounded-md border border-cream-500/40 px-3 py-1.5 text-body-sm text-cream-100 transition-colors hover:bg-cream-100/10 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40"
              data-testid="agent-browser-relaunch"
            >
              {t("agentBrowser.relaunch")}
            </button>
          </CenteredOverlay>
        ) : !showNoVnc ? (
          // Auto-launch in flight — defaultProfile exists, activeProfile
          // is null (waiting for the launch RPC round-trip + status
          // refresh). Spinner + "Starting the Lexiun Browser…" copy.
          <CenteredOverlay tone="info" testId="agent-browser-auto-launching">
            <Loader2 size={20} className="animate-spin text-cream-300" aria-hidden />
            <p className="text-body-sm text-cream-200">{t("agentBrowser.autoLaunching")}</p>
          </CenteredOverlay>
        ) : (
          // The actual live surface. h-full + w-full so RFB renders
          // edge-to-edge inside the main area; the canvas auto-scales
          // via scaleViewport per NoVncPane's RFB options.
          <NoVncPane
            vncEndpoint={vncEndpoint!}
            onStatusChange={(s) => setVncStatus(s)}
            className="h-full w-full bg-coffee-900"
          />
        )}
      </div>

      {/* ── Bottombar ──────────────────────────────────────────────
           Three columns. Center column carries an assertive 1-line
           trust message + an inline gold "LEARN MORE" link button.
           Left + right columns are empty spacers that keep the center
           column centered in the grid.
           Slim h-9. */}
      <footer
        data-testid="agent-browser-bottombar"
        className="grid h-9 shrink-0 grid-cols-[1fr_auto_1fr] items-center gap-3 border-t border-cream-200/60 bg-cream-50 px-3 text-caption dark:border-coffee-700/60 dark:bg-coffee-900"
      >
        <div aria-hidden />
        <div
          data-testid="agent-browser-bottombar-message"
          role="note"
          className="flex items-center justify-center gap-2 text-amber-700 dark:text-amber-300"
        >
          <span className="truncate text-center">
            {t("agentBrowser.bottombarAssertive")}
          </span>
          <button
            ref={learnMoreButtonRef}
            type="button"
            data-testid="agent-browser-learn-more-button"
            onClick={() => setLearnMoreOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={learnMoreOpen}
            className="inline-flex shrink-0 items-center rounded-md bg-gold-500 px-3 py-0.5 font-bold uppercase tracking-wide text-coffee-900 transition-colors hover:bg-gold-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-cream-50 dark:focus-visible:ring-offset-coffee-900"
          >
            {t("agentBrowser.learnMoreButton")}
          </button>
        </div>
        {/* Right-column spacer — kept so the center column stays
            centered in the grid (the prior stub-note indicator was
            removed with the runtime's `vnc.note` field per
            feedback_no_fallbacks). */}
        <div aria-hidden />
      </footer>

      <LearnMoreDialog
        open={learnMoreOpen}
        onClose={() => setLearnMoreOpen(false)}
        triggerRef={learnMoreButtonRef}
      />
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────

interface CenteredOverlayProps {
  tone: "info" | "warning" | "error";
  testId: string;
  role?: "alert" | "note";
  children: React.ReactNode;
}

/** Replaces the noVNC canvas when the browser isn't live (loading,
 *  error, locked, not-provisioned, auto-launching). Centered card on
 *  the dark coffee-900 main bg so it doesn't compete with the rest of
 *  the chrome — when the canvas paints, this is GONE entirely, not
 *  pushed-aside. */
function CenteredOverlay({ tone, testId, role, children }: CenteredOverlayProps) {
  return (
    <div
      data-testid={testId}
      data-tone={tone}
      role={role}
      className={cn(
        "flex max-w-lg flex-col items-center gap-2 rounded-lg border px-6 py-8 text-center",
        tone === "warning"
          ? "border-amber-900/60 bg-amber-950/40"
          : tone === "error"
            ? "border-red-900/60 bg-red-950/40"
            : "border-coffee-700/60 bg-coffee-800/40",
      )}
    >
      {children}
    </div>
  );
}

// ProfilePicker removed 2026-05-31 as part of the Option B retirement
// (lexruntime-openclaw-settings-implementation-plan.md §4-sexies). The
// SecurityWarning + ActorStrip components were removed 2026-06-01 in
// the browser-shines redesign — the security note moved into the
// bottombar (compact form), and the turn-taking status moved into the
// topbar's ActorStatusPill alongside the Pause/Close buttons.
// Operator-side profile management lives in LexPortal's
// UserRuntimePane Browser tab. If a beta surfaces a real "I need 2
// profiles" use case, the picker shape can be revived from git history
// (last present at 54413324).

interface ActorStatusPillProps {
  vncStatus: NoVncStatus;
  paused: boolean;
  agentNavigatingUrl: string | null;
}

/** Compact turn-taking indicator for the topbar. Drops the full
 *  ActorStrip surface — just the colored dot + label, no row card. The
 *  Pause/Close action buttons live alongside the pill in the topbar. */
function ActorStatusPill({
  vncStatus,
  paused,
  agentNavigatingUrl,
}: ActorStatusPillProps) {
  const { t } = useTranslation();
  // §7.3 turn-taking states. "Agent is navigating" takes precedence —
  // it's the strongest signal of what's happening on the canvas right
  // now.
  let label: string;
  let tone: "ok" | "warn" | "neutral";
  if (agentNavigatingUrl !== null && !paused) {
    label = t("agentBrowser.actor.agentNavigating", {
      url: shortUrlForActorStrip(agentNavigatingUrl),
    });
    tone = "neutral";
  } else {
    switch (vncStatus) {
      case "connected":
        if (paused) {
          label = t("agentBrowser.actor.paused");
          tone = "warn";
        } else {
          label = t("agentBrowser.actor.userControl");
          tone = "ok";
        }
        break;
      case "connecting":
        label = t("agentBrowser.actor.connecting");
        tone = "neutral";
        break;
      case "disconnected":
      case "error":
        label = t("agentBrowser.actor.disconnected");
        tone = "warn";
        break;
      default:
        label = t("agentBrowser.actor.idle");
        tone = "neutral";
    }
  }
  return (
    <span
      data-testid="agent-browser-actor-label"
      data-tone={tone}
      className={cn(
        "hidden items-center gap-2 text-caption font-medium md:inline-flex",
        tone === "ok"
          ? "text-emerald-700 dark:text-emerald-300"
          : tone === "warn"
            ? "text-amber-700 dark:text-amber-300"
            : "text-coffee-600 dark:text-cream-400",
      )}
    >
      <span
        aria-hidden
        className={cn(
          "inline-block h-2 w-2 rounded-full",
          tone === "ok"
            ? "bg-emerald-500"
            : tone === "warn"
              ? "bg-amber-500"
              : "bg-coffee-500",
        )}
      />
      {label}
    </span>
  );
}

// Visual helper for the D3 actor-pill live readout: trim the protocol +
// (when over 60 chars) the path so the label stays single-line without
// breaking long URLs pasted into the agent.
function shortUrlForActorStrip(url: string): string {
  const noProto = url.replace(/^https?:\/\//, "");
  if (noProto.length <= 60) return noProto;
  try {
    const parsed = new URL(url);
    return `${parsed.host}/...`;
  } catch {
    return `${noProto.slice(0, 57)}...`;
  }
}
